import { apiFetch } from './client';
import type { StatCardData, PersonaId } from './types';

export interface DashboardPayload {
  statStrip: StatCardData[];
  personas: Array<{
    id: PersonaId;
    name: string;
    side: 'agency' | 'ecosystem';
    description: string;
    featureAccess: string[];
  }>;
  experienceTypes: Array<{
    id: string;
    label: string;
    icon: string;
    featureCount: number;
    color: string;
    route?: string;
  }>;
  fundingFeed: Array<{
    id: string;
    agency: string;
    title: string;
    amount: string;
    tag: string;
    deadline: string;
  }>;
}

/**
 * Fetch the persona-specific dashboard payload for the active org.
 * The backend composes the payload from Gold-layer tables
 * (funding_stat_strip, compliance_status, work_zone_summary, etc.).
 * No mock fallback — when Gold is empty the UI renders an empty state.
 */
export function getDashboard(
  orgId: string,
  persona?: PersonaId,
): Promise<DashboardPayload> {
  const q = persona ? `?persona=${persona}` : '';
  return apiFetch<DashboardPayload>(`/api/v2/orgs/${orgId}/dashboard${q}`);
}

export function getPersonas(orgId: string) {
  return apiFetch<{ personas: DashboardPayload['personas'] }>(
    `/api/v2/orgs/${orgId}/personas`,
  );
}

export const EMPTY_DASHBOARD: DashboardPayload = {
  statStrip: [],
  personas: [],
  experienceTypes: [],
  fundingFeed: [],
};

/* ============================================================
 * V4 Dashboard API — SPEC-FRONTEND_V4 §7
 * No mock fallback. Empty responses render empty states.
 * ============================================================ */

/**
 * Phase C C4 — provenance tag attached per-row to dashboard tile data.
 *
 *   - `live`        → row is sourced from a non-seeded gold/silver table
 *                     populated by real ETL (e.g. peer_benchmark, NBI).
 *   - `demo`        → row is sourced from a seed-v1 tagged table
 *                     (compliance_status, asset_failure_probs,
 *                     incident_trends) OR an empty:true catalog table.
 */
export type ProvenanceTag = 'live' | 'demo';

export interface HomeResponse {
  kpis: Array<{
    title: string;
    value: string | number;
    unit?: string;
    trend?: 'up' | 'down' | 'neutral';
    trendPercent?: number;
    provenance?: ProvenanceTag;
    sourceTable?: string;
  }>;
  stateComparison: Array<{
    rank: number;
    state: string;
    kpi1: number;
    kpi2: number;
    kpi3: number;
    status: 'on-track' | 'at-risk' | 'critical';
    provenance?: ProvenanceTag;
    sourceTable?: string;
  }>;
  nationalBars: Array<{
    label: string;
    value: number;
    max: number;
    color: string;
    provenance?: ProvenanceTag;
    sourceTable?: string;
  }>;
  sparklines: Array<{
    title: string;
    data: number[];
    value: string;
    change: number;
    unit?: string;
    provenance?: ProvenanceTag;
    sourceTable?: string;
  }>;
  countdown?: { daysRemaining: number; label: string };
  /** Roll-up: 'demo' if any tile is demo, else 'live'. Useful for the hero. */
  provenanceSummary?: ProvenanceTag;
}

export interface FunctionResponse {
  key: string;
  kpis: HomeResponse['kpis'];
  painPoints: Array<{ title: string; description: string; relatedTools: string[] }>;
  intelCards: Array<{ title: string; insight: string; stat: string; action?: string }>;
  gisLayers: Array<{ id: string; name: string; color?: string }>;
}

export interface MapResponse {
  center: [number, number];
  zoom: number;
  pins: Array<{
    id: string;
    lat: number;
    lng: number;
    severity: 'critical' | 'warning' | 'info';
    label?: string;
  }>;
  layers: Array<{ id: string; name: string; enabled: boolean }>;
  stats?: { critical: number; warning: number; info: number; total: number };
}

export interface TickerResponse {
  items: Array<{ id: string; text: string; source?: string; url?: string }>;
}

export interface CountdownResponse {
  daysRemaining: number;
  targetDate: string;
  label: string;
}

export interface CustomizeUpdate {
  selectedItems: string[];
}

import { useAuthStore } from '@/stores/authStore';

/** Resolve active orgId from the auth store (falls back to 'org_public' for anon flows). */
function orgPath(): string {
  const orgId = useAuthStore.getState().user?.orgId || 'org_public';
  return encodeURIComponent(orgId);
}

export function getHome(state?: string): Promise<HomeResponse> {
  const q = state ? `?state=${encodeURIComponent(state)}` : '';
  return apiFetch<HomeResponse>(`/api/v2/orgs/${orgPath()}/dashboard/home${q}`);
}

export function getFunction(functionKey: string): Promise<FunctionResponse> {
  return apiFetch<FunctionResponse>(
    `/api/v2/orgs/${orgPath()}/dashboard/function/${encodeURIComponent(functionKey)}`,
  );
}

export function getFunctionKpis(functionKey: string): Promise<HomeResponse['kpis']> {
  return apiFetch<HomeResponse['kpis']>(
    `/api/v2/orgs/${orgPath()}/dashboard/function/${encodeURIComponent(functionKey)}/kpis`,
  );
}

export function getFunctionIntel(
  functionKey: string,
): Promise<FunctionResponse['intelCards']> {
  return apiFetch<FunctionResponse['intelCards']>(
    `/api/v2/orgs/${orgPath()}/dashboard/function/${encodeURIComponent(functionKey)}/intel`,
  );
}

export function getMap(
  functionKey?: string,
  state?: string,
): Promise<MapResponse> {
  const params: string[] = [];
  if (functionKey) params.push(`function=${encodeURIComponent(functionKey)}`);
  if (state) params.push(`state=${encodeURIComponent(state)}`);
  const q = params.length > 0 ? `?${params.join('&')}` : '';
  return apiFetch<MapResponse>(`/api/v2/orgs/${orgPath()}/dashboard/map${q}`);
}

export function getTicker(): Promise<TickerResponse> {
  return apiFetch<TickerResponse>(`/api/v2/orgs/${orgPath()}/dashboard/ticker`);
}

export function getCountdown(): Promise<CountdownResponse> {
  return apiFetch<CountdownResponse>(`/api/v2/orgs/${orgPath()}/dashboard/countdown`);
}

export interface ToolV4Response {
  key: string;
  solutions: Array<{
    id: string;
    painPoint: string;
    solution: string;
    gisIntegration: string;
    steps?: string[];
    estimatedTime?: string;
  }>;
}

export function getToolV4(
  orgId: string,
  toolKey: string,
): Promise<ToolV4Response> {
  return apiFetch<ToolV4Response>(
    `/api/v2/orgs/${encodeURIComponent(orgId)}/tools/${encodeURIComponent(toolKey)}`,
  );
}

export function updateCustomize(
  update: CustomizeUpdate,
): Promise<{ success: true; selectedItems: string[] }> {
  return apiFetch<{ success: true; selectedItems: string[] }>(
    '/api/v2/dashboard/customize',
    { method: 'POST', body: JSON.stringify(update) },
  );
}
